// Card — a raised surface with consistent padding and radius.
// Use for grouping related controls (forms, dashboard panels, list items).

import type { HTMLAttributes, ReactNode } from "react";

import { tokens } from "../tokens/tokens";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual weight.
   *  - "flat":     bordered, no shadow. For grouped form sections.
   *  - "subtle":   filled neutral background. For inline summaries.
   *  - "raised":   single soft shadow. For tappable list rows.
   *  - "elevated": multi-layer shadow, no border. For premium surfaces
   *                that should feel detached from the page (login,
   *                dialogs). */
  variant?: "flat" | "subtle" | "raised" | "elevated";
  /** Inner padding token. Default 4 (16px). */
  padding?: keyof typeof tokens.space;
  children?: ReactNode;
}

export function Card({
  variant = "flat",
  padding = 4,
  style,
  children,
  ...rest
}: CardProps) {
  const base: React.CSSProperties = {
    background:
      variant === "subtle"
        ? tokens.color.semantic.surfaceAlt
        : tokens.color.semantic.surface,
    border:
      variant === "flat" || variant === "subtle"
        ? `1px solid ${tokens.color.semantic.border}`
        : "none",
    borderRadius: variant === "elevated" ? tokens.radius.xl : tokens.radius.lg,
    boxShadow:
      variant === "raised"
        ? tokens.shadow.md
        : variant === "elevated"
          ? // Layered shadow — extremely soft, large diffuse radius.
            // The card sits lightly on the page rather than punching
            // out of it. Tasteful institutional weight, not pop-up
            // dialog drama.
            [
              "0 1px 0 rgba(15, 23, 42, 0.02)",
              "0 4px 12px rgba(15, 23, 42, 0.04)",
              "0 24px 56px rgba(15, 23, 42, 0.06)",
            ].join(", ")
          : "none",
    padding: tokens.space[padding],
  };
  return (
    <div style={{ ...base, ...style }} {...rest}>
      {children}
    </div>
  );
}
